import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'

// Endpoint resolution — explicit opt-in, no production default.
//
// Previously this fell back to https://tempest.lovedrop.live/graphql
// when VITE_TEMPEST_API_URL was unset. Combined with the seeded login
// credentials in Login.tsx, that meant `npm run dev` on a freshly cloned
// repo silently authenticated against the live production backend.
// Production builds picked up the same default if the deploy pipeline
// forgot to set the env var.
//
// New behavior:
//   - VITE_TEMPEST_API_URL wins if set (use this for prod + staging).
//   - In Vite dev mode (`npm run dev`) we fall back to localhost so the
//     local Rails server is the obvious target.
//   - Production builds with no VITE_TEMPEST_API_URL throw at module
//     load — better to fail loudly than misdirect to prod.
const explicitEndpoint = import.meta.env.VITE_TEMPEST_API_URL

const endpoint = (() => {
  if (typeof explicitEndpoint === 'string' && explicitEndpoint.length > 0) {
    return explicitEndpoint
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:3000/graphql'
  }
  throw new Error(
    'VITE_TEMPEST_API_URL must be set for production builds. ' +
    'Point it at your Tempest GraphQL endpoint (e.g. https://tempest.example.com/graphql).'
  )
})()

const httpLink = new HttpLink({ uri: endpoint })

// Auth header is populated from the JWT we stash in localStorage on
// successful login. Mutations that don't need auth (userLogin,
// userRegister, guest cart ops) work fine when the header is absent.
const authLink = setContext((_op, { headers }) => {
  const token = localStorage.getItem('tempest.authToken')
  return {
    headers: {
      ...(headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  }
})

export const apolloClient = new ApolloClient({
  link: from([authLink, httpLink]),
  cache: new InMemoryCache(),
  // Always go to network; we don't want stale livestream state.
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
    query:      { fetchPolicy: 'network-only' }
  }
})

export const apiEndpoint = endpoint
